import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  PaginationProvider,
  SearchProvider,
  SortProvider,
  ModalProvider,
  FilterProvider,
  Box,
  Spinner,
  MultiFileUploadProvider,
  PageViewProvider,
  MediaDragAndDropProvider,
  DropdownOptions,
  TooltipPopup,
} from 'dash-ui';
import { DEFAULT_SLIDESHOW_FOLDER_NAME } from 'dash-common';

import { useGetMediaFolderByIdQuery } from 'gql/generated.graphql';
import NotFoundPage from 'pages/flow/NotFoundPage/NotFoundPage';
import MediaFolderDetails from 'pages/properties/PropertyMediaFolderPage/components/MediaFolderDetails/MediaFolderDetails';
import SetFolderNameModal from 'containers/properties/PropertyMedia/components/SetFolderNameModal/SetFolderNameModal';
import RemoveFolderModal from 'containers/properties/PropertyMedia/components/RemoveFolderModal/RemoveFolderModal';
import MediaPageHeader from 'containers/properties/PropertyMedia/components/MediaPageHeader/MediaPageHeader';
import RemoveMediaFileModal from 'containers/properties/PropertyMedia/components/RemoveMediaFileModal/RemoveMediaFileModal';
import MoveFileToFolderModal from 'containers/properties/PropertyMedia/components/MoveFileToFolderModal/MoveFileToFolderModal';
import EditMediaFileModal from 'containers/properties/PropertyMedia/components/EditMediaFileModal/EditMediaFileModal';
import MediaFilesView from 'containers/properties/PropertyMedia/components/MediaFilesView/MediaFilesView';
import { useBrowserTabTitle } from 'common/hooks/use-browser-tab-title/use-browser-tab-title';

function PropertyMediaFolderPage() {
  const { id, folderId } = useParams();
  const { t } = useTranslation();

  const { data, loading } = useGetMediaFolderByIdQuery({
    variables: { folderId },
    fetchPolicy: 'network-only',
  });
  const folder = data?.getMediaFolderById;

  useBrowserTabTitle(`${folder?.folderName} ${t('common:labels.dashSuffix')}`);

  return (
    <Flex flexDirection="column" flex={1} w="100%">
      <DropdownOptions />
      <TooltipPopup />
      <PaginationProvider>
        <SearchProvider>
          <FilterProvider>
            <SortProvider>
              <ModalProvider>
                <PageViewProvider>
                  <MediaDragAndDropProvider>
                    <MultiFileUploadProvider>
                      <Box w="100%" pos="relative">
                        {loading && <Spinner size={32} fullContent />}
                        {folder && id ? (
                          <>
                            <MediaFolderDetails folder={folder} />
                            <MediaPageHeader
                              propertyId={id}
                              folderId={folderId}
                            />
                            <MediaFilesView
                              propertyId={id}
                              folderId={folderId}
                              isSlideshowFolder={
                                folder.folderName ===
                                DEFAULT_SLIDESHOW_FOLDER_NAME
                              }
                            />
                            <SetFolderNameModal
                              propertyId={id}
                              folderId={folderId}
                            />
                            <RemoveFolderModal />
                            <RemoveMediaFileModal />
                            <EditMediaFileModal />
                            <MoveFileToFolderModal />
                          </>
                        ) : (
                          <Box h={100}>
                            <NotFoundPage />
                          </Box>
                        )}
                      </Box>
                    </MultiFileUploadProvider>
                  </MediaDragAndDropProvider>
                </PageViewProvider>
              </ModalProvider>
            </SortProvider>
          </FilterProvider>
        </SearchProvider>
      </PaginationProvider>
    </Flex>
  );
}

export default PropertyMediaFolderPage;
